import React, { Component } from "react";
import { Upload, Table, Tag, Alert, Typography, message, Checkbox, Input, InputNumber, Select } from "antd";
import { UploadOutlined, FileExcelOutlined, DownloadOutlined } from "@ant-design/icons";
import axios from "axios";
import * as XLSX from "xlsx";
import { ModalItem, ButtonItem } from "../../../../Components/Items";
import AyudaConversion from "../../../../Components/AyudaConversion";

const { Text } = Typography;

const normalizar = (valor) => String(valor ?? "").trim().toUpperCase();

const parseNumero = (valor) => {
  if (valor === undefined || valor === null || valor === "") return 0;
  if (typeof valor === "number") return valor;
  const limpio = String(valor).replace(/[^0-9.-]/g, "");
  const num = parseFloat(limpio);
  return Number.isNaN(num) ? 0 : num;
};

const ALIAS_CLAVE = ["CLAVE", "CÓDIGO", "CODIGO"];
const ALIAS_DESCRIPCION = ["DESCRIPCION", "DESCRIPCIÓN"];
const ALIAS_CANTIDAD = ["CANTIDAD"];
const ALIAS_PRECIO = ["PRECIO UNITARIO", "PRECIO", "COSTO"];

const obtenerValorColumna = (fila, alias) => {
  const claves = Object.keys(fila);
  for (const texto of alias) {
    const clave = claves.find((k) => normalizar(k).includes(texto));
    if (clave !== undefined) return fila[clave];
  }
  return undefined;
};

class ModalCargarExcelOC extends Component {
  state = {
    cargando: false,
    creandoRelaciones: false,
    archivoNombre: null,
    filas: [], // { clave, descripcionExcel, cantidad, precioUnitario, estado, insumo, candidato, candidatos, propuesta, motivo }
  };

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.setState({ cargando: false, creandoRelaciones: false, archivoNombre: null, filas: [] });
    }
  }

  descargarPlantilla = () => {
    const worksheet = XLSX.utils.json_to_sheet([
      { Clave: "ABC123", Descripcion: "Producto de ejemplo", Cantidad: 10, "Precio Unitario": 150.5 },
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Plantilla");
    XLSX.writeFile(workbook, "Plantilla_Insumos_OrdenDeCompra.xlsx");
  };

  descargarNoEncontrados = () => {
    const noEncontrados = this.state.filas.filter((f) => f.estado === "no_encontrado");
    if (noEncontrados.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(
      noEncontrados.map((f) => ({
        Clave: f.clave,
        Descripcion: f.descripcionExcel,
        Cantidad: f.cantidad,
        "Precio Unitario": f.precioUnitario,
        Motivo: f.motivo || "No se encontró ningún insumo con esa clave",
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "No encontrados");
    XLSX.writeFile(workbook, "Insumos_No_Encontrados_OrdenDeCompra.xlsx");
  };

  onSubirArchivo = (info) => {
    const file = info.file;
    if (!/\.xlsx?$/i.test(file.name || "")) {
      message.error("El archivo debe ser un Excel (.xlsx/.xls)");
      return;
    }
    this.setState({ cargando: true, archivoNombre: file.name, filas: [] });

    const reader = new FileReader();
    reader.onloadend = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const primeraHoja = workbook.Sheets[workbook.SheetNames[0]];
        const filasCrudas = XLSX.utils.sheet_to_json(primeraHoja, { defval: "" });
        this.procesarFilas(filasCrudas);
      } catch (err) {
        console.error(err);
        message.error("No se pudo leer el archivo. Verifica que sea un Excel válido.");
        this.setState({ cargando: false });
      }
    };
    reader.readAsArrayBuffer(file.originFileObj || file);
  };

  construirPropuesta = (fila, candidato) => ({
    incluir: true,
    insumoId: candidato._id,
    codigo: fila.clave,
    descripcion: fila.descripcionExcel || candidato.descripcion || "",
    costo: fila.precioUnitario,
    conversion: 1,
    compraMin: undefined,
    claveUnidad: candidato.claveUnidadSugerida || "",
  });

  construirFila = (fila, resultado) => {
    const generico = !!resultado.generico;
    if (resultado.estado === "ENCONTRADO") {
      return { ...fila, estado: "encontrado", insumo: resultado.relacion, generico };
    }
    if (resultado.estado === "PROPONER") {
      return {
        ...fila,
        estado: "proponer",
        candidato: resultado.candidato,
        propuesta: this.construirPropuesta(fila, resultado.candidato),
        generico,
      };
    }
    if (resultado.estado === "AMBIGUO") {
      return { ...fila, estado: "ambiguo", candidatos: resultado.candidatos, generico };
    }
    return { ...fila, estado: "no_encontrado", motivo: "No se encontró ningún insumo con esa clave" };
  };

  procesarFilas = async (filasCrudas) => {
    const filasParseadas = filasCrudas
      .map((fila) => ({
        clave: String(obtenerValorColumna(fila, ALIAS_CLAVE) ?? "").trim(),
        descripcionExcel: String(obtenerValorColumna(fila, ALIAS_DESCRIPCION) ?? "").trim(),
        cantidad: parseNumero(obtenerValorColumna(fila, ALIAS_CANTIDAD)),
        precioUnitario: parseNumero(obtenerValorColumna(fila, ALIAS_PRECIO)),
      }))
      .filter((fila) => fila.clave);

    if (filasParseadas.length === 0) {
      message.error("No se encontraron filas con una columna de Clave reconocible.");
      this.setState({ cargando: false });
      return;
    }

    const { empresaId, headersToken, proveedorId } = this.props;

    try {
      const res = await axios.post(
        "insumos/resolverClavesProveedorMasivo",
        { empresaId, proveedorId, claves: filasParseadas.map((f) => f.clave) },
        { headers: headersToken }
      );

      if (res.data.success !== 2) {
        message.error(res.data.message || "No se pudieron resolver las claves del Excel.");
        this.setState({ cargando: false });
        return;
      }

      const filasResueltas = filasParseadas.map((fila, index) =>
        this.construirFila(fila, res.data.resultados[index] || { estado: "NO_ENCONTRADO" })
      );

      this.setState({ filas: filasResueltas, cargando: false });
    } catch (err) {
      console.error(err);
      message.error("Error de conexión al resolver las claves del Excel.");
      this.setState({ cargando: false });
    }
  };

  elegirCandidato = (index, candidato) => {
    this.setState((prevState) => {
      const filas = [...prevState.filas];
      const fila = filas[index];
      filas[index] = {
        ...fila,
        estado: "proponer",
        candidato,
        propuesta: this.construirPropuesta(fila, candidato),
      };
      return { filas };
    });
  };

  actualizarPropuesta = (index, campo, valor) => {
    this.setState((prevState) => {
      const filas = [...prevState.filas];
      filas[index] = { ...filas[index], propuesta: { ...filas[index].propuesta, [campo]: valor } };
      return { filas };
    });
  };

  crearRelacionesPropuestas = async () => {
    const { empresaId, headersToken, proveedorId } = this.props;
    const filasAIncluir = this.state.filas
      .map((fila, index) => ({ fila, index }))
      .filter(({ fila }) => fila.estado === "proponer" && fila.propuesta && fila.propuesta.incluir);

    if (filasAIncluir.length === 0) {
      message.error("No hay propuestas seleccionadas para crear.");
      return;
    }

    const propuestaIncompleta = filasAIncluir.find(({ fila }) => {
      const p = fila.propuesta;
      return !p.codigo || !p.descripcion || !p.claveUnidad || p.costo === undefined || p.costo === null || p.costo === "";
    });
    if (propuestaIncompleta) {
      message.error("Completa Clave, Descripción, Unidad y Costo en todas las propuestas seleccionadas.");
      return;
    }

    const itemsPorInsumoId = new Map();
    filasAIncluir.forEach(({ fila }) => {
      const { propuesta } = fila;
      if (!itemsPorInsumoId.has(propuesta.insumoId)) {
        itemsPorInsumoId.set(propuesta.insumoId, {
          insumoId: propuesta.insumoId,
          codigo: propuesta.codigo,
          descripcion: propuesta.descripcion,
          claveUnidad: propuesta.claveUnidad,
          costo: propuesta.costo,
          conversion: propuesta.conversion,
          compraMin: propuesta.compraMin,
        });
      }
    });

    this.setState({ creandoRelaciones: true });
    try {
      const res = await axios.post(
        "insumos/asignarProveedorMasivo",
        { empresaId, proveedorId, items: Array.from(itemsPorInsumoId.values()) },
        { headers: headersToken }
      );

      if (res.data.success !== 2) {
        message.error(res.data.message || "No se pudieron crear las relaciones.");
        this.setState({ creandoRelaciones: false });
        return;
      }

      const resultadosPorInsumoId = new Map();
      res.data.resultados.forEach((resultado) => resultadosPorInsumoId.set(resultado.insumoId, resultado));

      let creadas = 0;
      const filasActualizadas = this.state.filas.map((fila) => {
        if (fila.estado !== "proponer" || !fila.propuesta) return fila;
        const resultado = resultadosPorInsumoId.get(fila.propuesta.insumoId);
        if (!resultado) return fila;
        if (resultado.ok && resultado.relacion) {
          creadas += 1;
          return { ...fila, estado: "recien_creada", insumo: resultado.relacion };
        }
        return { ...fila, motivoCreacion: resultado.message };
      });

      this.setState({ filas: filasActualizadas, creandoRelaciones: false });
      if (creadas > 0) {
        message.success(`${creadas} relación(es) creada(s) correctamente.`);
      }
    } catch (err) {
      console.error(err);
      message.error("Error de conexión al crear las relaciones.");
      this.setState({ creandoRelaciones: false });
    }
  };

  confirmarCarga = () => {
    const validas = this.state.filas.filter((f) => f.estado === "encontrado" || f.estado === "recien_creada");
    if (validas.length === 0) {
      message.error("No hay líneas válidas para cargar");
      return;
    }
    this.props.onConfirmar(
      validas.map((f) => ({
        insumoId: f.insumo._id,
        insumoCompleto: f.insumo,
        cantidad: f.cantidad,
        precioUnitario: f.precioUnitario,
        descuento: 0,
        observaciones: f.generico ? [f.clave, f.descripcionExcel].filter(Boolean).join(" - ") : undefined,
      }))
    );
  };

  render() {
    const { visible, onCancel, proveedorId } = this.props;
    const { cargando, creandoRelaciones, archivoNombre, filas } = this.state;

    const filasIndexadas = filas.map((fila, index) => ({ ...fila, __index: index }));
    const validas = filas.filter((f) => f.estado === "encontrado" || f.estado === "recien_creada").length;
    const propuestas = filasIndexadas.filter((fila) => fila.estado === "proponer").map((fila) => ({ fila, index: fila.__index }));
    const propuestasIncluidas = propuestas.filter(({ fila }) => fila.propuesta.incluir).length;
    const noEncontrados = filas.filter((f) => f.estado === "no_encontrado").length;
    const sinResolver = filas.length - validas - propuestas.length;

    const columnas = [
      { title: "Clave", dataIndex: "clave", width: 110 },
      {
        title: "Insumo",
        render: (_, f) => {
          if (f.estado === "encontrado" || f.estado === "recien_creada") {
            if (f.generico) {
              return (
                <span>
                  {f.insumo.descripcion} <Text type="secondary">({f.descripcionExcel || f.clave})</Text>
                </span>
              );
            }
            return f.insumo.descripcion;
          }
          if (f.estado === "proponer" || f.estado === "ambiguo") return f.descripcionExcel || <Text type="secondary">—</Text>;
          return <Text type="secondary">—</Text>;
        },
      },
      { title: "Cantidad", dataIndex: "cantidad", width: 90 },
      {
        title: "Precio Unitario",
        dataIndex: "precioUnitario",
        width: 130,
        render: (v) => `$${Number(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`,
      },
      {
        title: "Estado",
        width: 260,
        render: (_, f) => {
          const tagGenerico = f.generico && (
            <Tag color="default" title={`Se usará el insumo genérico "${normalizar("ARTICULO")}" porque no se encontró la clave ${f.clave}`}>
              Genérico
            </Tag>
          );
          if (f.estado === "encontrado") return <>{<Tag color="green">Encontrado</Tag>}{tagGenerico}</>;
          if (f.estado === "recien_creada") return <>{<Tag color="green">Relación creada</Tag>}{tagGenerico}</>;
          if (f.estado === "proponer") return <>{<Tag color="orange">Proponer relación (ver abajo)</Tag>}{tagGenerico}</>;
          if (f.estado === "ambiguo") {
            return (
              <>
                <Select
                  placeholder="Clave ambigua: elegir insumo"
                  style={{ width: 230 }}
                  onChange={(insumoId) =>
                    this.elegirCandidato(f.__index, f.candidatos.find((c) => c._id === insumoId))
                  }
                >
                  {f.candidatos.map((c) => (
                    <Select.Option key={c._id} value={c._id}>
                      {c.clave + " - " + c.descripcion}
                    </Select.Option>
                  ))}
                </Select>
                {tagGenerico}
              </>
            );
          }
          return <Tag color="red">{f.motivoCreacion || f.motivo}</Tag>;
        },
      },
    ];

    const columnasPropuestas = [
      {
        title: "",
        width: 40,
        render: (_, { fila, index }) => (
          <Checkbox
            checked={fila.propuesta.incluir}
            onChange={(e) => this.actualizarPropuesta(index, "incluir", e.target.checked)}
          />
        ),
      },
      {
        title: "Clave",
        width: 110,
        render: (_, { fila, index }) => (
          <Input
            value={fila.propuesta.codigo}
            onChange={(e) => this.actualizarPropuesta(index, "codigo", e.target.value)}
          />
        ),
      },
      {
        title: "Descripción",
        render: (_, { fila, index }) => (
          <Input
            value={fila.propuesta.descripcion}
            onChange={(e) => this.actualizarPropuesta(index, "descripcion", e.target.value)}
          />
        ),
      },
      {
        title: "Unidad (SAT)",
        width: 110,
        render: (_, { fila, index }) => (
          <Input
            value={fila.propuesta.claveUnidad}
            onChange={(e) => this.actualizarPropuesta(index, "claveUnidad", e.target.value)}
          />
        ),
      },
      {
        title: "Costo",
        width: 110,
        render: (_, { fila, index }) => (
          <InputNumber
            style={{ width: "100%" }}
            value={fila.propuesta.costo}
            onChange={(v) => this.actualizarPropuesta(index, "costo", v)}
          />
        ),
      },
      {
        title: "Conversión",
        width: 100,
        render: (_, { fila, index }) => (
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <InputNumber
              style={{ width: "100%" }}
              value={fila.propuesta.conversion}
              onChange={(v) => this.actualizarPropuesta(index, "conversion", v)}
            />
            <AyudaConversion insumo={{ conversion: fila.propuesta.conversion }} />
          </span>
        ),
      },
      {
        title: "Compra Mín.",
        width: 100,
        render: (_, { fila, index }) => (
          <InputNumber
            style={{ width: "100%" }}
            value={fila.propuesta.compraMin}
            onChange={(v) => this.actualizarPropuesta(index, "compraMin", v)}
          />
        ),
      },
    ];

    return (
      <ModalItem
        title={
          <span>
            <FileExcelOutlined /> Cargar Insumos desde Excel
          </span>
        }
        visible={visible}
        onCancel={onCancel}
        width={950}
        spinning={cargando || creandoRelaciones}
      >
        <div style={{ padding: "0 15px 15px 15px" }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 15 }}
            message="El Excel debe tener columnas Clave, Descripcion, Cantidad y Precio Unitario"
            description='Si la Clave ya tiene relación con este proveedor se usa directamente. Si no, pero existe un insumo con esa misma clave, se propone crear la relación (puedes revisar y crear todas de una vez). Si la clave no corresponde a ningún insumo, la línea se carga usando el insumo genérico "ARTICULO" (marcado como "Genérico") con la clave y descripción originales guardadas en observaciones. Las líneas que se carguen reemplazarán cualquier insumo que ya esté agregado en esta orden de compra.'
          />

          {!proveedorId ? (
            <Alert type="warning" showIcon message="Selecciona primero un proveedor" />
          ) : (
            <>
              <ButtonItem
                icon={<DownloadOutlined />}
                onClick={this.descargarPlantilla}
                style={{ marginRight: 10 }}
              >
                Descargar plantilla
              </ButtonItem>

              <Upload showUploadList={false} customRequest={this.onSubirArchivo} accept=".xlsx,.xls">
                <ButtonItem icon={<UploadOutlined />}>Subir Excel</ButtonItem>
              </Upload>

              {archivoNombre && (
                <Text type="secondary" style={{ marginLeft: 10 }}>
                  {archivoNombre}
                </Text>
              )}

              {filas.length > 0 && (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      margin: "15px 0 10px",
                    }}
                  >
                    <Text>
                      {validas} línea(s) lista(s) para cargar
                      {propuestas.length > 0 ? `, ${propuestas.length} con relación por proponer` : ""}
                      {sinResolver > 0 ? `, ${sinResolver} no encontrada(s)` : ""}
                    </Text>
                    {noEncontrados > 0 && (
                      <ButtonItem icon={<DownloadOutlined />} onClick={this.descargarNoEncontrados}>
                        Descargar no encontrados ({noEncontrados})
                      </ButtonItem>
                    )}
                  </div>
                  <Table
                    size="small"
                    rowKey="__index"
                    columns={columnas}
                    dataSource={filasIndexadas}
                    pagination={{ pageSize: 8 }}
                  />
                </>
              )}

              {propuestas.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 10 }}
                    message="Estas claves no tienen relación con este proveedor, pero existe un insumo con esa clave"
                    description="Revisa/edita los datos de cada propuesta y crea las relaciones seleccionadas de una sola vez."
                  />
                  <Table
                    size="small"
                    rowKey={({ index }) => index}
                    columns={columnasPropuestas}
                    dataSource={propuestas}
                    pagination={false}
                  />
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                    <ButtonItem
                      type="primary"
                      loading={creandoRelaciones}
                      disabled={propuestasIncluidas === 0}
                      onClick={this.crearRelacionesPropuestas}
                    >
                      Crear {propuestasIncluidas} relación(es)
                    </ButtonItem>
                  </div>
                </div>
              )}
            </>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 15, gap: 10 }}>
            <ButtonItem onClick={onCancel} rojo>
              Cancelar
            </ButtonItem>
            <ButtonItem type="primary" disabled={validas === 0} onClick={this.confirmarCarga}>
              Cargar a la Orden ({validas})
            </ButtonItem>
          </div>
        </div>
      </ModalItem>
    );
  }
}

export default ModalCargarExcelOC;
